import React, { useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface ScheduleEntry {
  day: string;
  date: string;
  classId: string;
  className: string;
  subject: string;
  periodNumber: string;
  periodId: string;
  order: number;
  startTime: string;
  endTime: string;
  duration: number;
  type: string;
}

interface TeacherScheduleData {
  teacherId: string;
  teacherName: string;
  academicYear: string;
  schedules: ScheduleEntry[];
}

interface TeacherScheduleProps {
  domain: string;
}

const TeacherSchedule: React.FC<TeacherScheduleProps> = ({ domain }) => {
  const [teacherId, setTeacherId] = useState('');
  const [schedule, setSchedule] = useState<{ [day: string]: ScheduleEntry[] } | null>(null);
  const [teacherName, setTeacherName] = useState('');
  const [loading, setLoading] = useState(false);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const loadTeacherSchedule = async () => {
    if (!teacherId) {
      alert('Please enter a Teacher ID');
      return;
    }

    setLoading(true);
    try {
      // Query all schedule documents for this teacher
      const scheduleCollection = collection(db, `school-users/${domain}/teacher-schedules`);
      const q = query(scheduleCollection, where('teacherId', '==', teacherId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        alert('No schedule found for this teacher');
        setSchedule(null);
        setTeacherName('');
        return;
      }

      // Organize schedules by day
      const scheduleByDay: { [day: string]: ScheduleEntry[] } = {};
      let foundTeacherName = '';

      querySnapshot.forEach((doc) => {
        const data = doc.data() as TeacherScheduleData;
        if (!foundTeacherName) {
          foundTeacherName = data.teacherName;
        }

        // Group schedules by day
        data.schedules.forEach((entry) => {
          if (!scheduleByDay[entry.day]) {
            scheduleByDay[entry.day] = [];
          }
          scheduleByDay[entry.day].push(entry);
        });
      });

      // Sort periods within each day by order
      Object.keys(scheduleByDay).forEach((day) => {
        scheduleByDay[day].sort((a, b) => a.order - b.order);
      });

      setSchedule(scheduleByDay);
      setTeacherName(foundTeacherName);
    } catch (error) {
      console.error('Error loading teacher schedule:', error);
      alert('Failed to load teacher schedule');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!schedule) return;

    // Create CSV content
    let csvContent = `Teacher Schedule - ${teacherName} (${teacherId})\n\n`;
    csvContent += 'Day,Period,Time,Class,Subject,Type\n';

    days.forEach((day) => {
      if (schedule[day]) {
        schedule[day].forEach((entry) => {
          csvContent += `${day},${entry.periodNumber},${entry.startTime}-${entry.endTime},${entry.className},${entry.subject},${entry.type}\n`;
        });
      }
    });

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `teacher_schedule_${teacherId}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-calendar-week me-2 text-primary-custom"></i>
          Teacher Schedule
        </h5>
        <small className="text-muted">View individual teacher timetables and class assignments</small>
      </div>
      <div className="card-body">
        {/* Search Section */}
        <div className="row mb-4 g-3">
          <div className="col-md-8">
            <input
              type="text"
              className="form-control"
              placeholder="Enter Teacher ID (e.g., 17PVSS0001)"
              value={teacherId}
              onChange={(e) => setTeacherId(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <button
              className="btn btn-primary w-100"
              onClick={loadTeacherSchedule}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Loading...
                </>
              ) : (
                <>
                  <i className="bi bi-search me-2"></i>
                  Load Schedule
                </>
              )}
            </button>
          </div>
        </div>

        {/* Teacher Info and Export */}
        {schedule && teacherName && (
          <div className="alert alert-info d-flex justify-content-between align-items-center mb-4">
            <div>
              <strong>Teacher:</strong> {teacherName} <span className="text-muted">({teacherId})</span>
              <br />
              <small>Total Classes: {Object.keys(schedule).length} days scheduled</small>
            </div>
            <button className="btn btn-sm btn-outline-primary" onClick={exportToExcel}>
              <i className="bi bi-download me-2"></i>
              Export to CSV
            </button>
          </div>
        )}

        {/* Schedule Table */}
        {schedule && (
          <div className="table-responsive">
            <table className="table table-bordered table-hover">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '100px' }}>Day</th>
                  <th>Period 1</th>
                  <th>Period 2</th>
                  <th>Period 3</th>
                  <th>Period 4</th>
                  <th>Period 5</th>
                  <th>Period 6</th>
                  <th>Period 7</th>
                  <th>Period 8</th>
                </tr>
              </thead>
              <tbody>
                {days.map((day) => (
                  <tr key={day}>
                    <td className="fw-bold bg-light">{day}</td>
                    {schedule[day] ? (
                      <>
                        {schedule[day].map((entry, idx) => (
                          <td key={idx} className="small">
                            <div className="mb-1">
                              <strong className="text-primary-custom">{entry.subject}</strong>
                            </div>
                            <div className="text-muted small">{entry.className}</div>
                            <div className="text-muted small">
                              <i className="bi bi-clock me-1"></i>
                              {entry.startTime} - {entry.endTime}
                            </div>
                            <div>
                              <span className="badge badge-secondary-custom bg-secondary text-white small">
                                {entry.duration}m
                              </span>
                            </div>
                          </td>
                        ))}
                        {/* Fill empty cells */}
                        {Array.from({ length: Math.max(0, 8 - schedule[day].length) }).map((_, i) => (
                          <td key={`empty-${i}`} className="text-center text-muted">-</td>
                        ))}
                      </>
                    ) : (
                      <td colSpan={8} className="text-center text-muted">
                        No classes scheduled
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* No Data Message */}
        {!schedule && !loading && (
          <div className="text-center py-5">
            <i className="bi bi-calendar-x text-muted" style={{ fontSize: '3rem' }}></i>
            <p className="text-muted mt-3">Enter a Teacher ID to view their schedule</p>
          </div>
        )}

        {/* Help Section */}
        <div className="alert alert-light mt-4">
          <h6 className="alert-heading">
            <i className="bi bi-info-circle me-2"></i>
            How It Works
          </h6>
          <ul className="small mb-0">
            <li>Teacher schedules are <strong>automatically generated</strong> when class timetables are uploaded</li>
            <li>Each time a timetable is uploaded, the Cloud Function updates the teacher's schedule</li>
            <li>Teachers can view all their classes across different sections in one place</li>
            <li>Schedules show class assignments, subjects, and timing details</li>
            <li>Export the schedule to CSV for offline viewing or printing</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TeacherSchedule;
